import { DATA_TEST_ID, DATA_TEST_ID_TOAST } from "../_utils/data-testid";
import CheckIcon from "./assets/check.svg";
import CloseIcon from "./assets/close.svg";
import ErrorIcon from "./assets/error.svg";
import WarnIcon from "./assets/warn.svg";
import { cn } from "@khol/utils/tailwind/cn";

/**
 * If we don't get an `onClose` we don't show the Close Icon.
 *
 * If the text contains a long title or a description we align the icons to the top (not center)
 */
export function Toast({
  variant,
  title,
  description,
  htmlContent,
  onClose,
}: Props) {
  // set the defaults
  variant = variant || "no-icon";

  const withDescription = !!description;
  const withHtmlContent = !!htmlContent;
  const withLongTitle = title.length > 40;
  const alignStart = withDescription || withLongTitle;
  const variantClass = cn(
    "inline-flex h-8 w-8 flex-shrink-0 items-center justify-center  rounded-lg",
    {
      "bg-success-100 text-success-500": variant === "success",
      "bg-danger-100 text-danger-500": variant === "error",
      "bg-warning-100 text-warning-500": variant === "warn",
    }
  );

  return (
    <div
      className={cn(
        "flex w-full rounded-lg bg-default-50 p-4 text-default-700 shadow dark:shadow-none",
        {
          "items-center": !alignStart,
          "items-start": alignStart,
        }
      )}
      role="alert"
      {...{ [DATA_TEST_ID]: DATA_TEST_ID_TOAST }}
    >
      {variant !== "no-icon" && (
        <div className={variantClass} data-testid={variant}>
          {variant === "success" && <CheckIcon className="h-5 w-5" />}
          {variant === "warn" && <WarnIcon className="h-5 w-5" />}
          {variant === "error" && <ErrorIcon className="h-5 w-5" />}
          <span className="sr-only">Toast icon</span>
        </div>
      )}
      <div className="flex flex-col justify-start">
        <div className="ms-3 text-sm font-bold">{title}</div>
        {withHtmlContent ? (
          <>{htmlContent}</>
        ) : withDescription ? (
          <div className="ms-3 text-sm font-normal text-default-500">
            {description}
          </div>
        ) : null}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="-mx-1.5 -my-1.5 ms-auto inline-flex h-8 w-8 items-center justify-center rounded-lg bg-default-50 p-1.5 text-default-400 hover:bg-default-100 hover:text-default-900 focus:ring-2 focus:ring-default-300"
          aria-label="Close"
        >
          <span className="sr-only">Close</span>
          <CloseIcon className="h-3 w-3 stroke-default-400" />
        </button>
      )}
    </div>
  );
}

export type ToastVariant = "success" | "error" | "warn" | "no-icon";
export type Toast = {
  id: number;
  title: string;
  description?: string;
  variant?: ToastVariant;
  timeoutId: NodeJS.Timeout;
  htmlContent?: React.ReactNode;
};

interface Props {
  variant?: ToastVariant;
  title: string;
  description?: string;
  htmlContent?: React.ReactNode;
  onClose?: () => void;
}
